import { getAuthHeader } from '.';
import { ROOT_URL } from '../utils/constants';
import type { Event } from '../models/Event';
import type { AlpacaEvent } from '../models/AlpacaEvent';
import type { EventType } from '../models/EventType';

class EventApi {
  static async getEventTypes(): Promise<EventType[]> {
    const [headers] = await getAuthHeader();
    const response = await fetch(`${ROOT_URL}/api/eventtypes`, {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getEventsForUser(): Promise<Event[]> {
    const [headers] = await getAuthHeader();
    const response = await fetch(`${ROOT_URL}/api/events`, {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getEventById(id: number): Promise<Event> {
    const [headers] = await getAuthHeader();
    const response = await fetch(`${ROOT_URL}/api/events/${id}`, {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async createEvent(event: Event): Promise<Event> {
    const [headers] = await getAuthHeader();
    const response = await fetch(`${ROOT_URL}/api/events`, {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(event),
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async updateEvent(eventId: number, event: Event): Promise<Event> {
    const [headers] = await getAuthHeader();
    const response = await fetch(`${ROOT_URL}/api/events/${eventId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(event),
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async deleteEvent(eventId: number): Promise<boolean> {
    const [headers] = await getAuthHeader();
    const response = await fetch(`${ROOT_URL}/api/events/${eventId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: headers,
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return true;
  }
  static async getAlpacasForEvent(eventId: number): Promise<AlpacaEvent[]> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/events/${eventId}/alpacaevents`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpacaEvent(
    eventId: number,
    alpacaEventId: number,
  ): Promise<AlpacaEvent> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/events/${eventId}/alpacaevents/${alpacaEventId}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpacaEventsForAlpaca(
    alpacaId: number,
  ): Promise<AlpacaEvent[]> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/alpaca/${alpacaId}/alpacaevents`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async createAlpacaEvent(
    eventId: number,
    alpacaEvent: AlpacaEvent,
  ): Promise<AlpacaEvent> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/events/${eventId}/alpacaevents`,
      {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify(alpacaEvent),
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async createAlpacaEvents(
    eventId: number,
    alpacaEvents: AlpacaEvent[],
  ): Promise<AlpacaEvent[]> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/events/${eventId}/alpacaevents/multiple`,
      {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify(alpacaEvents),
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async updateAlpacaEvent(
    eventId: number,
    alpacaEvent: AlpacaEvent,
  ): Promise<AlpacaEvent> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/events/${eventId}/alpacaevents/${alpacaEvent.alpacaEventId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify(alpacaEvent),
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async deleteAlpacaEvent(
    eventId: number,
    alpacaEventId: number,
  ): Promise<boolean> {
    const [headers] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/events/${eventId}/alpacaevents/${alpacaEventId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return true;
  }
}

export default EventApi;
